import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  Text,
  VStack,
  IconButton,
  useToast,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { useOrganizationEnums, useUpdateOrganizationEnums } from '../../hooks/useOrganizationEnums';
import {
  ENUM_LABELS,
  formatEnumLabel,
  OrganizationEnums,
} from '../../utils/organizationEnums';

type EnumKey = keyof OrganizationEnums;

const SettingsPage: React.FC = () => {
  const { data: serverEnums, isLoading } = useOrganizationEnums();
  const updateEnums = useUpdateOrganizationEnums();
  const toast = useToast();
  const [draft, setDraft] = useState<OrganizationEnums | null>(null);
  const [newValues, setNewValues] = useState<Record<EnumKey, string>>({
    projectTypes: '',
    taskTypes: '',
    taskPriorities: '',
    taskStatuses: '',
  });

  const enums = draft ?? serverEnums;

  const handleAdd = (key: EnumKey) => {
    const value = newValues[key].trim();
    if (!value || !enums) return;

    const normalized = value.toLowerCase();
    if (enums[key].some((item) => item.toLowerCase() === normalized)) {
      toast({ title: 'Value already exists', status: 'warning', duration: 2500 });
      return;
    }

    setDraft({ ...enums, [key]: [...enums[key], normalized] });
    setNewValues((prev) => ({ ...prev, [key]: '' }));
  };

  const handleRemove = (key: EnumKey, index: number) => {
    if (!enums || enums[key].length <= 1) {
      toast({ title: 'At least one value is required', status: 'warning', duration: 2500 });
      return;
    }

    setDraft({
      ...enums,
      [key]: enums[key].filter((_, i) => i !== index),
    });
  };

  const handleSave = async () => {
    if (!enums) return;

    try {
      await updateEnums.mutateAsync(enums);
      setDraft(null);
      toast({ title: 'Settings saved', status: 'success', duration: 2500 });
    } catch (err: any) {
      toast({
        title: err.response?.data?.message || 'Failed to save settings',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (isLoading || !enums) {
    return <Text>Loading settings...</Text>;
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg" mb={2}>
          Settings
        </Heading>
        <Text color="gray.600">
          Configure the dropdown values used across projects and tasks in your workspace.
        </Text>
      </Box>

      {(Object.keys(ENUM_LABELS) as EnumKey[]).map((key) => (
        <Card key={key}>
          <CardHeader pb={2}>
            <Heading size="md">{ENUM_LABELS[key]}</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <HStack flexWrap="wrap" spacing={2}>
                {enums[key].map((value, index) => (
                  <Badge
                    key={`${key}-${value}-${index}`}
                    px={3}
                    py={1}
                    borderRadius="full"
                    colorScheme="blue"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    {formatEnumLabel(value)}
                    <IconButton
                      aria-label={`Remove ${value}`}
                      icon={<FaTrash />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleRemove(key, index)}
                    />
                  </Badge>
                ))}
              </HStack>

              <Divider />

              <HStack>
                <Input
                  placeholder={`Add ${ENUM_LABELS[key].toLowerCase().slice(0, -1)}...`}
                  value={newValues[key]}
                  onChange={(e) =>
                    setNewValues((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAdd(key);
                    }
                  }}
                />
                <Button leftIcon={<FaPlus />} onClick={() => handleAdd(key)}>
                  Add
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      ))}

      <HStack justify="flex-end">
        {draft && (
          <Button variant="ghost" onClick={() => setDraft(null)}>
            Reset changes
          </Button>
        )}
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={updateEnums.isPending}
          isDisabled={!draft}
        >
          Save settings
        </Button>
      </HStack>
    </VStack>
  );
};

export default SettingsPage;
